import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { TokenService } from './token.service';
import { IceServerConfiguration } from './webrtc.service';

export interface PendingSession {
  id: string;
  sessionId: string;
}

export interface SessionHistoryItem {
  serialNumber: string;
  note: string;
  timestamp: number;
  startTime: number;
  endTime: number;
}

export interface WebRtcSessionCredentials {
  signalingUrl: string;
  iceServers: IceServerConfiguration[];
  username: string;
  password: string;
}

@Injectable({ providedIn: 'root' })
export class PendingSessionsService {
  constructor(
    private http: HttpClient,
    private tokenService: TokenService
  ) {}

  private getAuthHeaders() {
    const token = this.tokenService.getToken() || environment.jwtToken;
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };
  }

  stopSession(id: string, serialNumber: string, notes: string = ''): Observable<void> {
    return this.http.post<void>(`https://${environment.backendUrl}/v1/machine/${serialNumber}/session/end`, { notes }, {
      headers: this.getAuthHeaders()
    });
  }

  renewSession(id: string, serialNumber: string): Observable<void> {
    return this.http.post<void>(
      `https://${environment.backendUrl}/v1/machine/${serialNumber}/extend/support`,
      {},
      {
        headers: this.getAuthHeaders()
      }
    );
  }

  getAllMachinePendingSessions(): Observable<PendingSession[]> {
    return this.http.get<string[]>(`https://${environment.backendUrl}/v1/machine/session`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map(arr => Array.isArray(arr)
        ? arr.filter(sessionId => sessionId && sessionId !== 'null').map((sessionId, idx) => ({ id: (idx + 1).toString(), sessionId }))
        : [])
    );
  }

  getMachineCredentials(serialNumber: string): Observable<WebRtcSessionCredentials> {
    return this.http.get<WebRtcSessionCredentials>(`https://${environment.backendUrl}/v1/machine/${serialNumber}/session/credentials`, {
      headers: this.getAuthHeaders()
    });
  }

  getSessionHistory(): Observable<SessionHistoryItem[]> {
    return this.http.get<SessionHistoryItem[]>(`https://${environment.backendUrl}/v1/machine/session/history`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map(items => Array.isArray(items) ? items : [])
    );
  }
}
